import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '../../../../core/pipes/translate.pipe';
import { RoundToTenthPipe } from '../../../../core/pipes/round-to-tenth.pipe';
import { Router } from '@angular/router';

interface OwnerInfo {
  id: string;
  name: string;
  type: string;
  role: string;
  phoneNumber: string;
  email: string;
  avatar?: string;
  performance?: any;
}

@Component({
  selector: 'app-booking-sidebar',
  standalone: true,
  imports: [CommonModule, TranslatePipe, RoundToTenthPipe],
  template: `
    <div class="w-full min-w-0 space-y-4 sm:space-y-6">
      <div class="surface-card p-4 sm:p-6 transition-all duration-300 relative overflow-hidden group">
        <!-- Subtle background glow -->
        <div class="absolute -top-24 -right-24 w-48 h-48 bg-primary/10 rounded-full blur-3xl group-hover:bg-primary/20 transition-all duration-500 pointer-events-none"></div>

        <div class="mb-6 relative z-10">
          <div class="text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight text-text-primary drop-shadow-sm font-mono break-words">
            {{ priceDisplay }}
          </div>
          <div class="flex items-center gap-2 mt-2 text-primary text-[11px] font-black uppercase tracking-widest font-mono">
            <span class="inline-block w-2 h-2 rounded-full bg-primary shadow-primary-glow animate-pulse"></span>
            {{ pricePeriod }}
          </div>
          @if (pricePerMeter) {
            <div class="inline-flex items-center gap-1.5 mt-3 px-2.5 py-1 bg-surface-elevated border border-border rounded-lg text-text-secondary font-mono text-[11px] font-bold shadow-sm">
              <i class="fa-solid fa-vector-square text-[10px] text-primary/70"></i>
              {{ pricePerMeter }}
            </div>
          }
          @if (securityDeposit) {
            <div class="inline-flex items-center gap-1.5 mt-3 sm:ml-2 px-2.5 py-1 bg-surface-elevated border border-border rounded-lg text-text-secondary font-mono text-[11px] font-bold shadow-sm">
              <i class="fa-solid fa-shield-alt text-[10px] text-primary/70"></i>
              Deposit: {{ securityDeposit | roundToTenth }} {{ priceDisplay.split(' ').pop() }}
            </div>
          }
          @if (commission && commission.commissionPercentage > 0) {
            <div class="inline-flex items-center gap-1.5 mt-3 sm:ml-2 px-2.5 py-1 bg-surface-elevated border border-border rounded-lg text-text-secondary font-mono text-[11px] font-bold shadow-sm">
              <i class="fa-solid fa-handshake text-[10px] text-primary/70"></i>
              Com: {{ commission.commissionPercentage }}%
            </div>
          }
        </div>

        <button
          type="button"
          (click)="bookNow.emit()"
          [disabled]="!hasSelectedDates"
          class="btn btn-primary w-full py-4 text-xs font-black uppercase tracking-widest relative overflow-hidden group"
          [class.shadow-primary-glow]="hasSelectedDates"
        >
          <div class="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out"></div>
          <span class="relative z-10 flex items-center justify-center gap-2">
            <i class="fa-solid fa-calendar-check text-sm"></i>
            <span>{{ hasSelectedDates ? ('unitDetails.sidebar.confirmBook' | translate) : ('unitDetails.sidebar.lockSelection' | translate) }}</span>
          </span>
        </button>

        @if (!hasSelectedDates) {
          <div class="mt-4 text-center text-[10px] font-bold text-warning bg-warning/10 border border-warning/20 rounded-xl py-2.5 px-3 flex items-center justify-center gap-2 animate-fadeIn shadow-sm">
            <i class="fa-solid fa-circle-exclamation animate-pulse"></i>
            <span class="uppercase tracking-wider">Select dates to continue</span>
          </div>
        }

        <div class="mt-5 pt-4 border-t border-border-subtle text-center text-[11px] font-bold uppercase tracking-wider text-text-muted flex items-center justify-center gap-2">
          <i class="fa-solid fa-shield-check text-[13px] text-success/80"></i>
          <span>{{ cancellationPolicy }}</span>
        </div>
      </div>

      <div class="surface-card p-4 sm:p-6">
        <h3 class="text-[11px] font-black text-text-secondary uppercase tracking-widest font-mono mb-5 flex items-center gap-2">
          <i class="fa-solid fa-id-badge text-primary/80"></i>
          {{ ownerSectionLabel | translate }}
        </h3>

        @if (owner) {
          <button
            class="w-full group text-left block focus:outline-none"
            type="button"
            (click)="openOwnerProfile()"
          >
              <div class="flex items-center gap-3 p-2.5 rounded-2xl border border-transparent hover:border-primary/30 hover:bg-surface-elevated transition-all duration-300 cursor-pointer shadow-sm min-w-0">
              <div class="w-12 h-12 bg-bg-secondary border border-border-strong rounded-xl flex items-center justify-center shrink-0 group-hover:scale-105 group-hover:border-primary/50 transition-all duration-300 relative overflow-hidden">
                @if (owner.avatar) {
                  <img [src]="owner.avatar" [alt]="owner.name" class="w-full h-full object-cover rounded-xl" (error)="owner.avatar = ''" />
                } @else {
                  <div class="absolute inset-0 bg-primary/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  <i class="fa-solid fa-user-tie text-base text-text-primary group-hover:text-primary transition-colors"></i>
                }
              </div>
              <div class="flex-1 min-w-0">
                <div class="text-sm font-black text-text-primary flex items-center gap-1.5 group-hover:text-primary transition-colors min-w-0">
                  <span class="truncate">{{ owner.name }}</span>
                  <i class="fa-solid fa-certificate text-primary text-[10px] shadow-sm"></i>
                </div>
                <div class="text-xs font-semibold text-text-secondary tracking-wide mt-0.5 truncate">
                  {{ formatRole(owner.role) }}
                </div>
              </div>
              <div class="w-7 h-7 rounded-lg bg-bg-secondary border border-border-subtle flex items-center justify-center text-text-muted group-hover:bg-primary group-hover:border-primary group-hover:text-white transition-all group-hover:shadow-primary-glow">
                <i class="fa-solid fa-chevron-right text-[10px]"></i>
              </div>
            </div>
          </button>

          <div class="grid grid-cols-1 min-[360px]:grid-cols-2 gap-3 mt-5">
            <a
              [href]="getPhoneLink()"
              class="flex flex-col items-center justify-center gap-1.5 p-3 bg-surface-elevated border border-border rounded-xl text-xs font-bold text-text-secondary hover:bg-success/10 hover:border-success/30 hover:text-success hover:-translate-y-0.5 transition-all shadow-sm group"
            >
              <i class="fa-solid fa-phone text-sm text-text-muted group-hover:text-success transition-colors"></i>
              <span class="text-[10px] uppercase tracking-wider">Call</span>
            </a>

            <a
              [href]="getEmailLink()"
              class="flex flex-col items-center justify-center gap-1.5 p-3 bg-surface-elevated border border-border rounded-xl text-xs font-bold text-text-secondary hover:bg-info/10 hover:border-info/30 hover:text-info hover:-translate-y-0.5 transition-all shadow-sm group"
            >
              <i class="fa-solid fa-envelope text-sm text-text-muted group-hover:text-info transition-colors"></i>
              <span class="text-[10px] uppercase tracking-wider">Message</span>
            </a>
          </div>

          @if (owner.performance) {
            <div class="grid grid-cols-3 gap-2 mt-4 pt-4 border-t border-border-subtle text-center min-w-0">
              <div>
                <div class="text-[10px] text-text-muted uppercase font-bold tracking-widest font-mono">Listings</div>
                <div class="text-xs font-black font-mono text-text-primary mt-1">{{ owner.performance.propertiesCount || 0 }}</div>
              </div>
              <div>
                <div class="text-[10px] text-text-muted uppercase font-bold tracking-widest font-mono">Rating</div>
                <div class="text-xs font-black font-mono text-text-primary mt-1 flex items-center justify-center gap-1">
                  {{ owner.performance.averageRating || 0 | roundToTenth }} <i class="fa-solid fa-star text-warning text-[10px]"></i>
                </div>
              </div>
              <div>
                <div class="text-[10px] text-text-muted uppercase font-bold tracking-widest font-mono">Response</div>
                <div class="text-[11px] font-black font-mono text-text-primary mt-1 truncate" [title]="owner.performance.responseTimeLabel">{{ owner.performance.responseTimeLabel || 'N/A' }}</div>
              </div>
            </div>
          }
        } @else {
          <div class="text-center py-6 bg-surface-elevated border border-border-subtle rounded-2xl">
            <div class="w-12 h-12 mx-auto bg-bg-secondary rounded-full flex items-center justify-center mb-3">
              <i class="fa-solid fa-user-slash text-lg text-text-muted"></i>
            </div>
            <p class="text-[10px] font-bold text-text-muted uppercase tracking-widest">Identity Suspended / Null</p>
          </div>
        }
      </div>

      <div class="surface-card bg-gradient-to-br from-surface to-surface-elevated rounded-3xl p-4 sm:p-6 border border-border-strong relative overflow-hidden group">
        <div class="absolute -right-4 -bottom-4 text-6xl text-primary/5 group-hover:text-primary/10 transition-colors duration-500 pointer-events-none">
          <i class="fa-solid fa-shield-halved"></i>
        </div>
        
        <div class="relative z-10 flex items-start gap-3 sm:gap-4 min-w-0">
          <div class="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary shrink-0 shadow-sm group-hover:scale-110 transition-transform">
            <i class="fa-solid fa-shield-heart text-base"></i>
          </div>
          <div class="space-y-3 min-w-0">
            <div class="text-[11px] font-black uppercase tracking-widest font-mono text-text-primary">
              {{ 'unitDetails.sidebar.safetyTips' | translate }}
            </div>
            <ul class="text-xs text-text-secondary space-y-2.5 list-none pl-0">
              <li *ngFor="let tip of safetyTips" class="flex items-start gap-2 leading-relaxed">
                <i class="fa-solid fa-check text-[10px] text-primary mt-1 opacity-70"></i>
                <span class="font-medium">{{ tip | translate }}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  `,
})
export class BookingSidebarComponent {
  readonly staticPhoneNumber: string = '010000000000';
  readonly staticEmail: string = 'homigo@gmail.com';

  @Input() priceDisplay: string = '';
  @Input() pricePeriod: string = '';
  @Input() pricePerMeter: string | null = null;
  @Input() securityDeposit: number | null = null;
  @Input() commission: any | null = null;
  @Input() cancellationPolicy: string = 'Free cancellation within 24 hours';
  @Input() owner: OwnerInfo | null = null;
  @Input() safetyTips: string[] = [
    "Don't pay before viewing the property physical structures.",
    'Verify official real-estate identity & documents.',
    'Execute all financial transactions using HomiGo escrow nodes.',
  ];
  @Input() hasSelectedDates: boolean = false;

  @Output() bookNow = new EventEmitter<void>();

  constructor(private router: Router) {}

  formatRole(role?: string): string {
    if (!role) return '';
    return role
      .replace(/[_-]+/g, ' ')
      .trim()
      .split(/\s+/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  get ownerSectionLabel(): string {
    const role = this.owner?.role?.toLowerCase?.() || '';
    if (role === 'real_estate_developer' || role === 'developer') {
      return 'unitDetails.sidebar.developer';
    } else if (role === 'broker') {
      return 'unitDetails.sidebar.broker';
    } else {
      return 'unitDetails.sidebar.owner';
    }
  }

  openOwnerProfile(): void {
    if (!this.owner?.id) return;

    const normalizedRole = this.owner.role?.toLowerCase?.() || '';
    const roleSegment =
      normalizedRole === 'property_seeker' || normalizedRole === 'seeker'
        ? 'seeker'
        : normalizedRole === 'real_estate_developer' || normalizedRole === 'developer'
          ? 'developer'
          : 'owner';

    this.router.navigate(['/accounts-information', roleSegment, this.owner.id]);
  }

  // ── FIX: Prioritize real owner context over local fallbacks ──
  getPhoneLink(): string {
    const activeNum = this.owner?.phoneNumber || this.staticPhoneNumber;
    const cleanNumber = activeNum.replace(/\D/g, '');
    return `tel:${cleanNumber}`;
  }

  // ── FIX: Prioritize real owner context over local fallbacks ──
  getEmailLink(): string {
    const activeEmail = this.owner?.email || this.staticEmail;
    const subject = encodeURIComponent(`Booking Inquiry - Property Rental`);
    const ownerName = this.owner?.name || 'there';
    const body = encodeURIComponent(
      `Hi ${ownerName},\n\nI'm interested in booking your property listed on HomiGo. Could you please provide more details about availability?\n\nThank you!`,
    );

    return `mailto:${activeEmail}?subject=${subject}&body=${body}`;
  }
}



